import type { Starred } from "@rhema/client";
import { Button, cn, EmptyState, focusRing, StarIcon, UiLink } from "@rhema/ui";
import { useCallback, useEffect, useState } from "react";
import { textOf } from "../conversation/MessageList";
import { chatTitle, firstName, listTime } from "../data/view";
import { useFeedback } from "../menus/Feedback";
import { WithMenu } from "../menus/WithMenu";
import { useClient, useClientState } from "../session/session";

/** Where a starred message opens: the chat at that message, or the thread it is in. */
export function messageHref(chatId: string, messageId: string): string {
  return `/m/${chatId}/${messageId}`;
}

/**
 * Your starred messages across every chat, newest star first, a page at a time. Each opens its
 * chat at the message.
 */
export function StarredMessages({ desktop }: { desktop: boolean }) {
  const client = useClient();
  const state = useClientState();
  const feedback = useFeedback();
  const [items, setItems] = useState<Starred[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (before?: string) => {
      setBusy(true);
      setError(null);
      try {
        const page = await client.starred(before);
        setItems((list) => (before ? [...(list ?? []), ...page.items] : page.items));
        setNext(page.next);
      } catch (e) {
        setError((e as Error).message || "Your starred messages could not be loaded.");
      } finally {
        setBusy(false);
      }
    },
    [client],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const unstar = (s: Starred) =>
    void client
      .starMessage(s.chatId, s.message.id, false)
      .then(() => {
        setItems((list) => (list ?? []).filter((x) => x.message.id !== s.message.id));
        feedback.notify("Removed from Starred.", () => {
          void client
            .starMessage(s.chatId, s.message.id, true)
            .then(() => void load())
            .catch((e: Error) => feedback.notify(e.message));
        });
      })
      .catch((e: Error) => feedback.notify(e.message));

  const where = (s: Starred) => {
    const chat = state.chats[s.chatId];
    if (!chat) return "A chat";
    return chat.kind === "channel" ? `#${chat.title}` : chatTitle(chat, state.me?.id);
  };
  const who = (s: Starred) => {
    if (s.message.senderId === state.me?.id) return "You";
    const user = state.chats[s.chatId]?.members.find((m) => m.user.id === s.message.senderId)?.user;
    return user ? firstName(user.display_name) : "";
  };

  return (
    <section aria-label="Starred messages" className={cn("flex flex-col", desktop ? "px-2.5" : "px-3")}>
      <h2 className="m-0 px-2.5 pt-4 pb-2 text-[13px] font-semibold text-ink-secondary">Starred messages</h2>
      {error && (
        <p
          role="alert"
          className="m-0 mx-2.5 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet"
        >
          {error}
        </p>
      )}
      {items === null && !error && <p className="m-0 px-2.5 text-sm text-ink-meta">Loading.</p>}
      {items?.length === 0 && (
        <EmptyState icon={<StarIcon size={26} />} title="No starred messages">
          Star a message from its menu to keep it here.
        </EmptyState>
      )}
      {items && items.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
          {items.map((s) => (
            <WithMenu
              key={`${s.chatId}-${s.message.id}`}
              label="this starred message"
              items={() => [{ id: "unstar", label: "Unstar", shortcut: "Alt+S", onSelect: () => unstar(s) }]}
              buttonClassName="right-2"
            >
              <UiLink
                href={messageHref(s.chatId, s.message.id)}
                className={cn(
                  "flex flex-col gap-0.5 rounded-tile px-2.5 py-2.5 text-ink no-underline hover:bg-surface-header/60",
                  focusRing,
                )}
              >
                <span className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate text-[14px] font-bold">{where(s)}</span>
                  <span className="flex-none text-xs text-ink-meta">{listTime(s.message.createdAt)}</span>
                </span>
                <span className="line-clamp-2 text-[14px] text-ink-secondary">
                  {who(s) && <b className="font-semibold text-ink">{who(s)}: </b>}
                  {textOf(s.message)}
                </span>
              </UiLink>
            </WithMenu>
          ))}
        </ul>
      )}
      {next && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 self-center"
          disabled={busy}
          onClick={() => void load(next)}
        >
          {busy ? "Loading" : "Show more"}
        </Button>
      )}
    </section>
  );
}
